import React from 'react';
import { Route, Switch, useParams, useRouteMatch } from 'react-router-dom';
import config from '../../configs/config';
import { useUpdateWhenScheduleAdvances } from '../../data/hooks/datetimeHooks';
import { useRequiredEnrichments } from '../../data/hooks/enrichments/useRequiredEnrichments';
import { useEvent } from '../../data/hooks/eventHooks';
import { useIsValidWaitingRoomSessionFilter } from '../../data/hooks/schedule/useIsValidWaitingRoomSessionFilter';
import { useExternalURL } from '../../data/hooks/ui/useExternalURL';
import { useShowEventMapOnSidePanel } from '../../data/hooks/ui/useShowEventMapOnSidePanel';
import { useShowFloorplanOnSidePanel } from '../../data/hooks/ui/useShowFloorplanOnSidePanel';
import { useSidepanelWallpaper } from '../../data/hooks/ui/useSidepanelWallpaper';
import { useVirtualRoom } from '../../data/hooks/ui/useVirtualRoom';
import LoadProgram from '../loaders/LoadProgram';
import HasEvent from '../validate/HasEvent';
import AutoOpenVirtualRoom from './AutoOpenVirtualRoom';
import Wallpaper from './common/Wallpaper/Wallpaper';
import ActiveMeetingWarningDialog from './dialogs/ActiveMeetingWarningDialog/ActiveMeetingWarningDialog';
import ActiveOnlineSessionWarningDialog from './dialogs/ActiveOnlineSessionWarningDialog';
import SelectProgramDialog from './dialogs/SelectProgramDialog';
import EmptySidePanel from './EmptySidePanel/EmptySidePanel';
import EventMap from './EventMap/EventMap';
import EventWaitingRoom from './EventWaitingRoom/EventWaitingRoom';
import ExternalSource from './ExternalSource/ExternalSource';
import FloorPlan from './FloorPlan/FloorPlan';
import { useNextSession, useSessionInProgress } from './hooks';
import RecentNotifications from './RecentNotifications/RecentNotifications';
import SidePanelWrapper from './SidePanelWrapper';
import { useIsDemoMode } from '../../data/hooks/ui/useIsDemoMode';

const EventSidePanel = (props) => {
    const { eventId } = useParams(),
        { path } = useRouteMatch(),
        event = useEvent(eventId),
        externalURL = useExternalURL(),
        showEventMap = useShowEventMapOnSidePanel(),
        showFloorplan = useShowFloorplanOnSidePanel(),
        currentSession = useSessionInProgress(eventId),
        isValidWaitingRoomSession = useIsValidWaitingRoomSessionFilter(eventId),
        currentWaitingRoomSession = useSessionInProgress(eventId, {
            filter: isValidWaitingRoomSession,
        }),
        virtualRoom = useVirtualRoom(),
        nextSession = useNextSession(eventId, {
            filter: isValidWaitingRoomSession,
        }),
        wallpaper = useSidepanelWallpaper(),
        requiredEnrichments = useRequiredEnrichments(event.id),
        isDemo = useIsDemoMode()

    useUpdateWhenScheduleAdvances(eventId);

    // prevent render when route is overridden
    if (event.is_closed || (!isDemo && !event.isWelcomeScreenShown && event.welcome_text) || (!isDemo && requiredEnrichments.length && !config.skipRequiredEnrichments)) {
        return (
            <SidePanelWrapper>
                <Wallpaper />
            </SidePanelWrapper>
        );
    }

    return (
        <SidePanelWrapper key={eventId}>
            {!wallpaper ? <AutoOpenVirtualRoom eventId={eventId} currentSession={currentSession} /> : null}
            {!externalURL ? <Route path={path} component={Wallpaper} /> : null}
            {!wallpaper ? (
                <>
                    {!externalURL || virtualRoom?.programId != currentSession?.id ? (
                        <Route path={path}>
                            <ActiveMeetingWarningDialog eventId={eventId} />
                            <ActiveOnlineSessionWarningDialog />
                            <SelectProgramDialog />
                        </Route>
                    ) : null}
                    <Switch>
                        {showEventMap ? <Route path={path} component={EventMap} /> : null}
                        {externalURL ? <Route path={path} component={ExternalSource} /> : null}
                        {event?.exhibitors_map_url ? <Route path={`${path}/sponsor/:path`} component={FloorPlan} /> : null}
                        {event?.exhibitors_map_url ? <Route path={`${path}/exhibitors/:path`} component={FloorPlan} /> : null}
                        {event?.exhibitors_map_url ? <Route path={`${path}/content/:path+`} component={FloorPlan} /> : null}
                        {event?.exhibitors_map_url ? <Route path={`${path}/exhibitors`} component={FloorPlan} /> : null}
                        {event?.exhibitors_map_url && showFloorplan ? <Route path={path} component={FloorPlan} /> : null}
                        <Route path={path}>{isValidWaitingRoomSession(currentWaitingRoomSession) || isValidWaitingRoomSession(nextSession) ? <EventWaitingRoom /> : <EmptySidePanel />}</Route>
                    </Switch>
                    <RecentNotifications eventId={eventId} />
                </>
            ) : null}
        </SidePanelWrapper>
    );
};

export default (props) => {
    const { eventId } = useParams();
    return (
        <HasEvent key={eventId} eventId={eventId} alt={null}>
            <LoadProgram eventId={eventId}>
                <EventSidePanel />
            </LoadProgram>
        </HasEvent>
    );
};
